/**
 * Admin Providers - Twilio Management API
 *
 * Zod schemas for external service provider management (Twilio).
 * Allows admins to view/manage Twilio subaccounts, phone numbers, and cleanup orphaned resources.
 *
 * Base path: /api/admin-panel/providers
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Twilio subaccount info with DB link status
 */
export const TwilioSubaccountInfoSchema = z.object({
  sid: z.string(),
  friendlyName: z.string(),
  status: z.string(),
  dateCreated: z.string(),
  dateUpdated: z.string(),
  // DB linkage
  linkedToOrganization: z.boolean(),
  organizationId: z.string().nullable(),
  organizationName: z.string().nullable(),
  apiKeyConnectionId: z.string().nullable(),
  apiKeyConnectionName: z.string().nullable(),
  phoneNumberCount: z.number().int(),
  whatsappSenderCount: z.number().int(),
  // Environment detection
  possibleOrgId: z.string().nullable(),
  orgExistsInThisDb: z.boolean()
});

/**
 * List subaccounts response
 */
export const TwilioSubaccountsResponseSchema = z.object({
  subaccounts: z.array(TwilioSubaccountInfoSchema),
  summary: z.object({
    total: z.number().int(),
    linked: z.number().int(),
    orphaned: z.number().int(),
    thisEnv: z.number().int(),
    otherEnv: z.number().int()
  })
});

/**
 * Phone number info
 */
export const TwilioPhoneNumberSchema = z.object({
  sid: z.string(),
  phoneNumber: z.string(),
  friendlyName: z.string(),
  capabilities: z.object({
    voice: z.boolean(),
    sms: z.boolean(),
    mms: z.boolean(),
    fax: z.boolean()
  }),
  dateCreated: z.string()
});

/**
 * Phone numbers response
 */
export const TwilioPhoneNumbersResponseSchema = z.object({
  phoneNumbers: z.array(TwilioPhoneNumberSchema)
});

/**
 * Subaccount details response
 */
export const TwilioSubaccountDetailsSchema = z.object({
  sid: z.string(),
  friendlyName: z.string(),
  status: z.string(),
  dateCreated: z.string(),
  balance: z.string().nullable(),
  usage: z
    .object({
      totalMessages: z.number().int(),
      totalCalls: z.number().int(),
      totalCost: z.string()
    })
    .nullable()
});

/**
 * Generic success/failure response
 */
export const TwilioOperationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string()
});

// ═══════════════════════════════════════════════════════════════
// WABA AUDIT SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const WABA_AUDIT_STATUSES = ["ok", "mismatch", "missing", "unverifiable"] as const;
export type WabaAuditStatus = (typeof WABA_AUDIT_STATUSES)[number];

export const WabaAuditEntrySchema = z.object({
  connectionId: z.string().uuid(),
  connectionName: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  /** wabaId from connection settings (canonical source after migration from credentials) */
  settingsWabaId: z.string().nullable(),
  senderWabaIds: z.array(z.string()),
  status: z.enum(WABA_AUDIT_STATUSES),
  suggestedWabaId: z.string().nullable()
});

export const WabaAuditResponseSchema = z.object({
  entries: z.array(WabaAuditEntrySchema),
  summary: z.object({
    total: z.number().int(),
    ok: z.number().int(),
    mismatch: z.number().int(),
    missing: z.number().int(),
    unverifiable: z.number().int()
  })
});

export const PatchWabaBodySchema = z.object({
  wabaId: z.string().min(1, "WABA ID required")
});

// ═══════════════════════════════════════════════════════════════
// PHONE NUMBER DRIFT SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Outcome of comparing one stored `PhoneNumber.number` against the number Twilio
 * reports for the same `sid`.
 *
 * `missing` and `unverifiable` are deliberately different states, and conflating
 * them is the mistake this audit exists to avoid. A SID that 404s on the master
 * account root is usually a subaccount-owned number, not an absent one; only a
 * SID absent from every account we could actually query is `missing`, and a SID
 * we could not query at all (no subaccount credentials, Twilio error, region
 * client unconfigured) is `unverifiable`. Neither is ever presented as drift,
 * because neither yields a number to correct the row TO.
 */
export const PHONE_NUMBER_DRIFT_STATUSES = ["ok", "drift", "missing", "unverifiable"] as const;
export type PhoneNumberDriftStatus = (typeof PHONE_NUMBER_DRIFT_STATUSES)[number];

export const PhoneNumberDriftEntrySchema = z.object({
  id: z.string().uuid(),
  /** Twilio IncomingPhoneNumber SID — the join key, and the only field both sides agree on. */
  sid: z.string(),
  /** What the `PhoneNumber` row claims the number is. */
  storedNumber: z.string(),
  /** What Twilio reports for `sid`; null for `missing` and `unverifiable`. */
  twilioNumber: z.string().nullable(),
  friendlyName: z.string().nullable(),
  countryCode: z.string(),
  /** Twilio API routing region recorded on the row (`us1` / `ie1`). */
  region: z.string(),
  organizationId: z.string().nullable(),
  /** Owning Twilio subaccount connection; null means the number should be on master. */
  apiKeyConnectionId: z.string().uuid().nullable(),
  status: z.enum(PHONE_NUMBER_DRIFT_STATUSES),
  /** Why the row is `missing` or `unverifiable`. Null for `ok` and `drift`. */
  reason: z.string().nullable()
});

export const PhoneNumberDriftResponseSchema = z.object({
  entries: z.array(PhoneNumberDriftEntrySchema),
  summary: z.object({
    total: z.number().int(),
    ok: z.number().int(),
    drift: z.number().int(),
    missing: z.number().int(),
    unverifiable: z.number().int()
  })
});

/**
 * Result of re-pointing one row's number at Twilio's value.
 *
 * The new number is NOT accepted from the caller — the server re-reads it from
 * Twilio inside the same request, so the client cannot dictate what a row is
 * corrected to, and a stale audit page cannot write a value Twilio no longer
 * reports. `changed: false` is the idempotent re-run: the row already matched.
 */
export const ReconcilePhoneNumberDriftResponseSchema = z.object({
  id: z.string().uuid(),
  sid: z.string(),
  previousNumber: z.string(),
  number: z.string(),
  changed: z.boolean()
});

// ═══════════════════════════════════════════════════════════════
// REQUEST SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * Path vars for subaccount endpoints
 */
export const SubaccountPathVarsSchema = z.object({
  sid: z.string()
});

/**
 * Path vars for connection-scoped endpoints (apiKeyConnectionId)
 */
export const ConnectionPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

/**
 * Path vars for sender delete (Twilio sender SID, e.g. XE...)
 */
export const SenderPathVarsSchema = z.object({
  senderId: z.string().min(1)
});

/**
 * WhatsApp sender list item (admin list by connection)
 */
export const WhatsappSenderListItemSchema = z.object({
  id: z.string(),
  senderId: z.string(),
  name: z.string(),
  wabaId: z.string(),
  phoneNumberId: z.string().uuid(),
  phoneNumber: z.string()
});

export const ListConnectionWhatsappSendersResponseSchema = z.object({
  senders: z.array(WhatsappSenderListItemSchema)
});

/**
 * Update subaccount body
 */
export const UpdateSubaccountBodySchema = z.object({
  // Trimmed before the length gate — same reason as the policy name: `.min(1)`
  // on its own admits "   ", and a whitespace-only rename would reach Twilio.
  friendlyName: z.string().trim().min(1, "Friendly name required")
});

/**
 * Path vars for phone-number release (Twilio IncomingPhoneNumber SID, e.g. PN...)
 */
export const TwilioPhoneSidPathVarsSchema = z.object({
  sid: z.string().min(1)
});

/**
 * Released phone number summary (audit-friendly)
 */
export const ReleasePhoneNumberResponseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  // This immediate-release use case only ever transitions ACTIVE -> RELEASED,
  // but the field mirrors PhoneNumberEntity.status and must stay assignable
  // from the entity. Derived from the generated enum rather than restated:
  // hand-listing the values made adding RELEASING to the column a type error
  // here, in a file that has nothing to do with the release lifecycle.
  status: z.enum(DbEnum.PHONE_NUMBER_STATUS_VALUES),
  releasedAt: z.string().nullable(),
  releasedBy: z.string().nullable()
});

// ═══════════════════════════════════════════════════════════════
// API SCHEMA OBJECT - Export for TApi generation
// ═══════════════════════════════════════════════════════════════

export const ZAdminProviders = {
  /**
   * List all Twilio subaccounts
   * GET /api/admin-panel/providers/twilio/subaccounts
   */
  ListTwilioSubaccounts: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/subaccounts",
    Response: ApiSuccessSchema(TwilioSubaccountsResponseSchema)
  },

  /**
   * Close orphaned Twilio subaccount
   * DELETE /api/admin-panel/providers/twilio/subaccounts/:sid
   */
  CloseOrphanedSubaccount: {
    method: "DELETE" as const,
    path: "/api/admin-panel/providers/twilio/subaccounts/:sid",
    PathVars: SubaccountPathVarsSchema,
    Response: ApiSuccessSchema(TwilioOperationResponseSchema)
  },

  /**
   * Release a phone number (soft delete + Twilio release, stops billing)
   * DELETE /api/admin-panel/providers/twilio/phone-numbers/:phoneId
   */
  ReleasePhoneNumber: {
    method: "DELETE" as const,
    path: "/api/admin-panel/providers/twilio/phone-numbers/:sid",
    PathVars: TwilioPhoneSidPathVarsSchema,
    Response: ApiSuccessSchema(ReleasePhoneNumberResponseSchema)
  },

  /**
   * Get phone numbers for a subaccount
   * GET /api/admin-panel/providers/twilio/subaccounts/:sid/phone-numbers
   */
  GetSubaccountPhoneNumbers: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/subaccounts/:sid/phone-numbers",
    PathVars: SubaccountPathVarsSchema,
    Response: ApiSuccessSchema(TwilioPhoneNumbersResponseSchema)
  },

  /**
   * Update subaccount (rename)
   * PATCH /api/admin-panel/providers/twilio/subaccounts/:sid
   */
  UpdateSubaccount: {
    method: "PATCH" as const,
    path: "/api/admin-panel/providers/twilio/subaccounts/:sid",
    PathVars: SubaccountPathVarsSchema,
    Body: UpdateSubaccountBodySchema,
    Response: ApiSuccessSchema(TwilioOperationResponseSchema)
  },

  /**
   * Get subaccount details
   * GET /api/admin-panel/providers/twilio/subaccounts/:sid/details
   */
  GetSubaccountDetails: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/subaccounts/:sid/details",
    PathVars: SubaccountPathVarsSchema,
    Response: ApiSuccessSchema(TwilioSubaccountDetailsSchema)
  },

  /**
   * List WhatsApp senders for a connection (admin)
   * GET /api/admin-panel/providers/twilio/connections/:connectionId/whatsapp-senders
   */
  GetConnectionWhatsappSenders: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/connections/:connectionId/whatsapp-senders",
    PathVars: ConnectionPathVarsSchema,
    Response: ApiSuccessSchema(ListConnectionWhatsappSendersResponseSchema)
  },

  /**
   * Delete WhatsApp sender (Twilio + DB). Admin only.
   * DELETE /api/admin-panel/providers/twilio/senders/:senderId
   */
  DeleteWhatsappSender: {
    method: "DELETE" as const,
    path: "/api/admin-panel/providers/twilio/senders/:senderId",
    PathVars: SenderPathVarsSchema,
    Response: ApiSuccessSchema(TwilioOperationResponseSchema)
  },

  /**
   * List unassigned phone numbers
   * GET /api/admin-panel/phone-numbers/unassigned?countryCode=BE
   * Used to assign bulk-purchased phones to organizations
   */
  ListUnassignedPhoneNumbers: {
    method: "GET" as const,
    path: "/api/admin-panel/phone-numbers/unassigned",
    Params: z.object({
      countryCode: z.string().length(2).toUpperCase().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        phoneNumbers: z.array(
          z.object({
            number: z.string(),
            sid: z.string(),
            countryCode: z.string(),
            friendlyName: z.string().nullable()
          })
        ),
        total: z.number()
      })
    )
  },
  /**
   * List orphan phone-number candidates: ACTIVE numbers billing with no active
   * deployment and no WhatsApp sender, past a grace window. Review before release.
   * GET /api/admin-panel/phone-numbers/orphan-candidates
   */
  ListOrphanPhoneNumberCandidates: {
    method: "GET" as const,
    path: "/api/admin-panel/phone-numbers/orphan-candidates",
    Response: ApiSuccessSchema(
      z.object({
        candidates: z.array(
          z.object({
            id: z.string().uuid(),
            number: z.string(),
            sid: z.string(),
            friendlyName: z.string().nullable(),
            countryCode: z.string(),
            createdAt: z.string().datetime(),
            apiKeyConnectionId: z.string().nullable(),
            organizationId: z.string().nullable()
          })
        ),
        total: z.number(),
        /** Subset of `total` with no resolvable org — cleared by the backfill. */
        unresolvedOrgCount: z.number()
      })
    )
  },
  /**
   * Audit every ACTIVE stored phone number against Twilio.
   * GET /api/admin-panel/phone-numbers/number-drift
   *
   * Read-only. `PhoneNumber.sid` is the join key; the audit reports whether the
   * stored `number` still matches what Twilio says that SID is.
   */
  GetPhoneNumberDrift: {
    method: "GET" as const,
    path: "/api/admin-panel/phone-numbers/number-drift",
    Response: ApiSuccessSchema(PhoneNumberDriftResponseSchema)
  },
  /**
   * Re-point one drifted row's number at the value Twilio reports for its SID.
   * POST /api/admin-panel/phone-numbers/number-drift/:sid/reconcile
   *
   * No body: the corrected value comes from Twilio, never from the caller.
   */
  ReconcilePhoneNumberDrift: {
    method: "POST" as const,
    path: "/api/admin-panel/phone-numbers/number-drift/:sid/reconcile",
    PathVars: TwilioPhoneSidPathVarsSchema,
    Response: ApiSuccessSchema(ReconcilePhoneNumberDriftResponseSchema)
  },
  TransferPhoneToConnection: {
    method: "POST" as const,
    path: "/api/admin-panel/providers/twilio/phones/:phoneId/transfer",
    PathVars: z.object({
      phoneId: z.string().uuid()
    }),
    Body: z.object({
      connectionId: z.string().uuid(),
      bundleSid: z.string().optional(),
      addressSid: z.string().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.boolean(),
        phoneNumber: z.string(),
        fromAccount: z.string(),
        toAccount: z.string()
      })
    )
  },
  GetConnectionDetails: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/connections/:connectionId/details",
    PathVars: z.object({
      connectionId: z.string().uuid()
    }),
    Response: ApiSuccessSchema(
      z.object({
        connection: z.object({
          id: z.string(),
          name: z.string(),
          service: z.string(),
          organizationId: z.string()
        }),
        organization: z.object({
          id: z.string(),
          name: z.string()
        }),
        phoneNumbers: z.array(
          z.object({
            id: z.string(),
            number: z.string(),
            sid: z.string(),
            friendlyName: z.string().nullable(),
            countryCode: z.string(),
            ownership: z.enum(["pool", "this", "other"]),
            ownerName: z.string().nullable(),
            whatsappSender: z
              .object({
                id: z.string(),
                senderId: z.string(),
                name: z.string(),
                wabaId: z.string()
              })
              .nullable()
          })
        ),
        whatsappSenders: z.array(
          z.object({
            id: z.string(),
            senderId: z.string(),
            name: z.string(),
            wabaId: z.string(),
            phoneNumber: z.string()
          })
        ),
        twilioSubaccount: z
          .object({
            sid: z.string(),
            friendlyName: z.string(),
            status: z.string(),
            dateCreated: z.string()
          })
          .nullable()
      })
    )
  },

  /**
   * WABA ID audit: all TWILIO connections with credential wabaId vs sender wabaId
   * GET /api/admin-panel/providers/twilio/waba-audit
   */
  GetWabaAudit: {
    method: "GET" as const,
    path: "/api/admin-panel/providers/twilio/waba-audit",
    Response: ApiSuccessSchema(WabaAuditResponseSchema)
  },

  /**
   * Patch wabaId on a specific connection
   * PATCH /api/admin-panel/providers/twilio/connections/:connectionId/waba
   */
  PatchConnectionWaba: {
    method: "PATCH" as const,
    path: "/api/admin-panel/providers/twilio/connections/:connectionId/waba",
    PathVars: ConnectionPathVarsSchema,
    Body: PatchWabaBodySchema,
    Response: ApiSuccessSchema(TwilioOperationResponseSchema)
  }
} as const;

// ═══════════════════════════════════════════════════════════════
// EXPORTED TYPES - For convenience
// ═══════════════════════════════════════════════════════════════

export type TwilioSubaccountInfo = z.infer<typeof TwilioSubaccountInfoSchema>;
export type TwilioSubaccountsResponse = z.infer<typeof TwilioSubaccountsResponseSchema>;
export type TwilioPhoneNumber = z.infer<typeof TwilioPhoneNumberSchema>;
export type TwilioPhoneNumbersResponse = z.infer<typeof TwilioPhoneNumbersResponseSchema>;
export type TwilioSubaccountDetails = z.infer<typeof TwilioSubaccountDetailsSchema>;
export type TwilioOperationResponse = z.infer<typeof TwilioOperationResponseSchema>;
export type UpdateSubaccountBody = z.infer<typeof UpdateSubaccountBodySchema>;
export type WabaAuditEntry = z.infer<typeof WabaAuditEntrySchema>;
export type WabaAuditResponse = z.infer<typeof WabaAuditResponseSchema>;
export type PatchWabaBody = z.infer<typeof PatchWabaBodySchema>;
export type PhoneNumberDriftEntry = z.infer<typeof PhoneNumberDriftEntrySchema>;
export type PhoneNumberDriftResponse = z.infer<typeof PhoneNumberDriftResponseSchema>;
export type ReconcilePhoneNumberDriftResponse = z.infer<
  typeof ReconcilePhoneNumberDriftResponseSchema
>;
